import React, { useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { List } from 'react-window';
import { Bookmark, FileText, Upload } from 'lucide-react';
import { getContainerColor } from '../utils/colors';
import { highlightText, formatTimestamp } from '../utils/helpers';

const LogLine = ({ log, isBookmarked, showContainers, filterText, timestampMode, onToggleBookmark, onLineClick }) => {
  const formattedTimestamp = formatTimestamp(log.timestamp, timestampMode);

  return (
    <div className="log-line group" id={`log-${log.id}`}>
      <div
        className={`bookmark-gutter ${isBookmarked ? 'active' : 'opacity-0 group-hover:opacity-100'}`}
        onClick={(e) => {
          e.stopPropagation();
          onToggleBookmark(log.id);
        }}
      >
        <Bookmark size={14} fill={isBookmarked ? 'currentColor' : 'none'} />
      </div>
      {showContainers && (
        <div
          className={`container-col ${getContainerColor(log.container)}`}
          title={log.container}
        >
          {log.container}
        </div>
      )}
      <div 
        className="content-col cursor-pointer"
        onClick={() => onLineClick && onLineClick(log)}
      >
        {formattedTimestamp && <span className="timestamp">{formattedTimestamp}</span>}
        <span
          className="text-slate-300"
          dangerouslySetInnerHTML={{ __html: highlightText(log.message, filterText) }}
        />
      </div>
    </div>
  );
};

LogLine.propTypes = {
  log: PropTypes.shape({
    id: PropTypes.number.isRequired,
    container: PropTypes.string.isRequired,
    timestamp: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired
  }).isRequired,
  isBookmarked: PropTypes.bool.isRequired,
  showContainers: PropTypes.bool.isRequired,
  filterText: PropTypes.string.isRequired,
  timestampMode: PropTypes.string.isRequired,
  onToggleBookmark: PropTypes.func.isRequired,
  onLineClick: PropTypes.func
};

const LogViewer = ({
  logs,
  hasLogs,
  showContainers,
  filterText,
  timestampMode,
  isBookmarked,
  onToggleBookmark,
  bookmarks,
  onJumpToLog,
  onLineClick,
  onImportClick
}) => {
  const listRef = useRef(null);

  // Calculate positions for off-screen bookmarks - must be called before any returns
  const bookmarkIndicators = useMemo(() => {
    if (!bookmarks || bookmarks.length === 0 || logs.length === 0) return [];

    // Create a map of visible log IDs for quick lookup
    const visibleLogIds = new Set(logs.map(log => log.id));

    // Find bookmarks that are not visible
    const offScreenBookmarks = bookmarks.filter(bm => !visibleLogIds.has(bm.logId));

    if (offScreenBookmarks.length === 0) return [];

    // Calculate the time range of visible logs
    const visibleTimestamps = logs.map(log => log.tsValue || new Date(log.timestamp).getTime()).filter(ts => ts > 0);
    if (visibleTimestamps.length === 0) return [];

    const minVisibleTime = Math.min(...visibleTimestamps);
    const maxVisibleTime = Math.max(...visibleTimestamps);
    const timeRange = maxVisibleTime - minVisibleTime;

    // For each off-screen bookmark, calculate where it would appear based on timestamp
    return offScreenBookmarks.map(bm => {
      const bmTime = new Date(bm.timestamp).getTime();

      // Determine if bookmark is before or after visible range
      if (bmTime < minVisibleTime) {
        // Bookmark is above visible area
        return {
          bookmark: bm,
          position: 'top',
          percentage: 0
        };
      } else if (bmTime > maxVisibleTime) {
        // Bookmark is below visible area
        return {
          bookmark: bm,
          position: 'bottom',
          percentage: 100
        };
      } else if (timeRange > 0) {
        // Bookmark is within time range but filtered out
        const percentage = ((bmTime - minVisibleTime) / timeRange) * 100;
        return {
          bookmark: bm,
          position: 'within',
          percentage
        };
      }
      return null;
    }).filter(Boolean);
  }, [bookmarks, logs]);

  // Early returns after hooks
  if (!hasLogs) {
    return (
      <div className="flex-1 overflow-y-auto bg-[#0d1117] relative font-mono">
        <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500">
          <FileText size={48} className="mb-4 opacity-50" />
          <p className="text-lg mb-2">No logs loaded</p>
          <p className="text-sm mb-6">Import a file to get started</p>
          <button
            onClick={onImportClick}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-2 transition"
          >
            <Upload size={16} />
            Import Logs
          </button>
        </div>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto bg-[#0d1117] relative font-mono">
        <div className="p-8 text-center text-slate-500">
          No logs match your filters.
        </div>
      </div>
    );
  }

  // Row component for react-window v2
  const Row = ({ index, style }) => {
    const log = logs[index];
    return (
      <div style={style} className={!showContainers ? 'hide-containers' : ''}>
        <LogLine
          log={log}
          isBookmarked={isBookmarked(log.id)}
          showContainers={showContainers}
          filterText={filterText}
          timestampMode={timestampMode}
          onToggleBookmark={onToggleBookmark}
          onLineClick={onLineClick}
        />
      </div>
    );
  };

  return (
    <div className="flex-1 bg-[#0d1117] relative font-mono overflow-hidden">
      <List
        listRef={listRef}
        rowComponent={Row}
        rowCount={logs.length}
        rowHeight={24}
        rowProps={{}}
        overscanCount={5}
        className="scrollbar-thin"
        style={{
          height: '100%',
          width: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0
        }}
      />

      {/* Render bookmark indicators for off-screen bookmarks */}
      {bookmarkIndicators.map(({ bookmark, position, percentage }) => (
        <div
          key={bookmark.id}
          className="absolute left-0 right-0 h-[2px] bg-yellow-500/60 hover:bg-yellow-400 cursor-pointer z-10 bookmark-indicator"
          style={{
            top: position === 'top' ? '0px' : position === 'bottom' ? 'calc(100% - 2px)' : `${percentage}%`,
            transition: 'background-color 0.2s'
          }}
          onClick={() => onJumpToLog(bookmark.logId)}
          title={`Bookmark: ${bookmark.note || 'No note'} (${formatTimestamp(bookmark.timestamp, timestampMode) || bookmark.timestamp})`}
        >
          <div className="absolute left-2 top-0 bottom-0 flex items-center">
            <Bookmark size={12} fill="currentColor" className="text-yellow-500" />
          </div>
        </div>
      ))}
    </div>
  );
};

LogViewer.propTypes = {
  logs: PropTypes.arrayOf(PropTypes.object).isRequired,
  hasLogs: PropTypes.bool.isRequired,
  showContainers: PropTypes.bool.isRequired,
  filterText: PropTypes.string.isRequired,
  timestampMode: PropTypes.string.isRequired,
  isBookmarked: PropTypes.func.isRequired,
  onToggleBookmark: PropTypes.func.isRequired,
  bookmarks: PropTypes.arrayOf(PropTypes.object),
  onJumpToLog: PropTypes.func,
  onLineClick: PropTypes.func,
  onImportClick: PropTypes.func
};

export default LogViewer;

